import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '../../types';
import { RootState } from '../../app/store';
import * as authService from '../../services/authService';
import { clearOrder, selectHasMeaningfulOrder } from '../order/orderSlice';
import i18n from '@/i18n';
import type { RegisterResponse } from '../../services/authService';
import { discountApi } from '../../services/discountApi';
import { logging } from '../../services/logging';
import { analytics } from '../../services/analytics';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  temporaryUserId: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

interface LoginErrorPayload {
  code?: string;
  message?: string;
  email?: string;
  status?: number;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  temporaryUserId: null,
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<User, { credentials: any }, { state: RootState; rejectValue: LoginErrorPayload }>(
  'auth/login',
  async ({ credentials }, { dispatch, getState, rejectWithValue }) => {
    const { temporaryUserId } = getState().auth;
    const payload = { ...credentials, temporaryUserId };

    try {
      const user = await authService.login(payload);
      
      // Only dispatch setAuthCredentials if the request succeeds
      dispatch(setAuthCredentials(user));

      // Apply referral discount if code exists
      const referralCode = typeof window !== 'undefined' ? localStorage.getItem('tulasi.referralCode') : null;
      if (referralCode) {
        try {
          const result = await discountApi.applyReferralCode(referralCode);
          if (!result.success) {
            logging.warn('Referral discount not applied:', { message: result.message });
          } else {
            analytics.track('referral_discount_applied', { referralCode });
          }
        } catch (error) {
          // Ignore referral application errors, user is still logged in
          logging.warn('Failed to apply referral discount:', { error });
        }
        localStorage.removeItem('tulasi.referralCode');
      }

      return user;
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData && typeof responseData === 'object') {
        return rejectWithValue({
          code: typeof responseData.code === 'string' ? responseData.code : undefined,
          message: typeof responseData.message === 'string' ? responseData.message : undefined,
          email: typeof responseData.email === 'string' ? responseData.email : undefined,
          status: typeof error?.response?.status === 'number' ? error.response.status : undefined,
        });
      }
      throw error;
    }
  }
);

export const register = createAsyncThunk<RegisterResponse, { credentials: any }, { state: RootState; rejectValue: LoginErrorPayload }>(
  'auth/register',
  async ({ credentials }, { getState, rejectWithValue }) => {
    const { temporaryUserId } = getState().auth;
    const payload = { ...credentials, temporaryUserId };

    const referralCode = typeof window !== 'undefined' ? localStorage.getItem('tulasi.referralCode') : null;
    if (referralCode) {
      payload.referralCode = referralCode;
    }

    try {
      const response = await authService.register(payload);

      if (referralCode) {
        localStorage.removeItem('tulasi.referralCode');
      }

      return response;
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData && typeof responseData === 'object') {
        return rejectWithValue({
          code: typeof responseData.code === 'string' ? responseData.code : undefined,
          message: typeof responseData.message === 'string' ? responseData.message : undefined,
          email: typeof responseData.email === 'string' ? responseData.email : undefined,
          status: typeof error?.response?.status === 'number' ? error.response.status : undefined,
        });
      }
      throw error;
    }
  }
);

// Performs logout and clears order if present, without UI confirmation.
export const logoutAndClear = createAsyncThunk<void, void, { state: RootState }>(
  'auth/logoutAndClear',
  async (_payload, { dispatch, getState }) => {
    const hasIncompleteOrder = selectHasMeaningfulOrder(getState());
    await authService.logout();
    if (hasIncompleteOrder) {
      dispatch(clearOrder());
    }
    dispatch(logoutSuccess());
  }
);

export const logoutWithGuard = createAsyncThunk<void, void, { state: RootState }>(
  'auth/logoutWithGuard',
  async (_payload, { dispatch, getState }) => {
    const hasIncompleteOrder = selectHasMeaningfulOrder(getState());
    if (hasIncompleteOrder) {
      const msg = i18n.t('auth.logout.confirmLoseOrder',
        'You have an unfinished order. Logging out will discard it. Continue?');
      const proceed = typeof window !== 'undefined' ? window.confirm(msg) : true;
      if (!proceed) return;
    }

    await authService.logout();
    if (hasIncompleteOrder) {
      dispatch(clearOrder());
    }
    dispatch(logoutSuccess());
  }
);

export const updatePreferredLanguage = createAsyncThunk<User, string, { state: RootState }>(
  'auth/updatePreferredLanguage',
  async (language) => {
    return await authService.updateLanguage(language);
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTemporaryUserId: (state, action: PayloadAction<string>) => {
      state.temporaryUserId = action.payload;
    },
    setAuthCredentials: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.status = 'succeeded';
      state.error = null;
      // After successful auth, temp history should be migrated server-side; clear the client copy
      state.temporaryUserId = null;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem('tulasi.hasLoggedInBefore', 'true');
        } catch {
          // ignore storage write failures
        }
      }
    },
    setUserLanguage: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user = { ...state.user, language: action.payload };
      }
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.status = 'idle';
      state.error = null;
      state.temporaryUserId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Login failed';
      });
    builder
      .addCase(register.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Registration failed';
      })
      .addCase(updatePreferredLanguage.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { setAuthCredentials, setUserLanguage, logoutSuccess, setTemporaryUserId } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectTemporaryUserId = (state: RootState) => state.auth.temporaryUserId;

export default authSlice.reducer;
